import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom"; // Para detectar la ruta actual
import {
  createEvento,
  getEventos,
  getEventosPorFecha,
  getEventosPorID,
  updateEvento,
  deleteEvento,
} from "../services/eventosService";

const EventosContext = createContext();

export const useEventos = () => useContext(EventosContext);

export const EventosProvider = ({ children }) => {
  const location = useLocation();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventosLoaded, setEventosLoaded] = useState(false); // Controla si los eventos ya se cargaron

  // Verifica si la ruta actual es `/eventos` o `/detalles`
  const isEventosPage = location.pathname.startsWith("/eventos") || location.pathname.startsWith("/detalles") || location.pathname.startsWith("/dashboard");

  // Optimizado: Solo obtiene eventos si la página es `/eventos` o `/detalles`
  const fetchEventos = useCallback(async (force = false) => {
    if ((isEventosPage && !eventosLoaded) || force) {
      setLoading(true);
      try {
        const response = await getEventos();
        setEventos(response.data || []);
        setEventosLoaded(true);
      } catch (err) {
        setError(err.response?.data?.message || "Error al obtener eventos");
      } finally {
        setLoading(false);
      }
    }
  }, [eventosLoaded]);

  // const fetchEventos = async () => {
  //   setLoading(true);
  //   try {
  //     const response = await getEventos();
  //     setEventos(response.data || []); // Asume que `data` contiene la lista de eventos
  //   } catch (error) {
  //     console.error("Error al obtener eventos:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Obtiene eventos por fecha sin modificar el estado global
  // const fetchEventosPorFecha = useCallback(async (fecha) => {
  //   setLoading(true);
  //   try {
  //     const response = await getEventosPorFecha(fecha);
  //     return response.data || [];
  //   } catch (err) {
  //     console.error("Error al obtener eventos por fecha:", err);
  //     setError(err.response?.data?.message || "Error al obtener eventos");
  //     return [];
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);
  const fetchEventosPorFecha = useCallback(async (fecha) => {
    setLoading(true);
    try {
      const response = await getEventosPorFecha(fecha);
      setEventos(response.data || []);
      return response.data || [];
    } catch (error) {
      setError(error.response?.data?.message || "Error al obtener eventos");
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtiene evento por ID sin modificar el estado global
  const fetchEventosPorID = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await getEventosPorID(id);
      return response.data || null;
    } catch (err) {
      setError(err.response?.data?.message || "Error al obtener el evento");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Agrega un nuevo evento y actualiza la lista sin recargar toda la página
  const addEvento = useCallback(async (formDataToSend) => {
    setLoading(true);
    try {
      const response = await createEvento(formDataToSend);
      setEventos((prevEventos) => [...prevEventos, response]); // Agrega sin recargar todo
      await fetchEventos(); // Recargar los eventos desde el backend
      return response;
    } catch (err) {
      setError(err.response?.data?.message || "Error al crear el evento");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // const addEvento = async (formDataToSend) => {
  //   setLoading(true);
  //   try {
  //     const response = await createEvento(formDataToSend);
  //     setEventos((prevEventos) => [...prevEventos, response]);
  //     await fetchEventos(); // Recargar los eventos desde el backend
  //     setError(null);
  //   } catch (err) {
  //     setError(err.response?.data?.message || "Error al crear el evento");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Actualiza un evento sin recargar toda la lista
  const updateEventoById = useCallback(async (id, formDataToSend) => {
    setLoading(true);
    try {
      const response = await updateEvento(id, formDataToSend);
      setEventos((prevEventos) =>
        prevEventos.map((evento) =>
          evento.id === id ? { ...evento, ...response.data } : evento
        )
      );
      await fetchEventos(); // Recargar los eventos desde el backend
      return response;
    } catch (err) {
      setError(err.response?.data?.message || "Error al actualizar el evento");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Elimina un evento sin hacer una recarga completa
  const deleteEventoById = useCallback(async (id) => {
    setLoading(true);
    try {
      await deleteEvento(id);
      setEventos((prevEventos) =>
        prevEventos.filter((evento) => evento.id !== id)
      );
      await fetchEventos(); // Recargar los eventos desde el backend
    } catch (err) {
      setError(err.response?.data?.message || "Error al eliminar el evento");
    } finally {
      setLoading(false);
    }
  }, []);

  // Solo carga eventos cuando la página es `/eventos` o `/detalles`
  useEffect(() => {
    if (isEventosPage) {
      fetchEventos();
    }
  }, [isEventosPage, fetchEventos]);

  return (
    <EventosContext.Provider
      value={{
        eventos,
        fetchEventos,
        fetchEventosPorFecha,
        fetchEventosPorID,
        addEvento,
        updateEventoById,
        deleteEventoById,
        loading,
        error,
      }}
    >
      {children}
    </EventosContext.Provider>
  );
};
